import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const DynamicDialogContext = createContext(null);

export function DynamicDialogProvider({ children }) {
  const [dialogs, setDialogs] = useState([]);

  const openDialog = useCallback((config) => {
    const id =
      config?.id ||
      `dialog-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    setDialogs((prev) => [...prev, { ...config, id }]);
    return id;
  }, []);

  const closeDialog = useCallback((dialogId) => {
    setDialogs((prev) => {
      if (!dialogId) return prev.slice(0, -1);
      return prev.filter((dialog) => dialog.id !== dialogId);
    });
  }, []);

  const closeAllDialogs = useCallback(() => {
    setDialogs([]);
  }, []);

  const value = useMemo(
    () => ({
      dialogs,
      openDialog,
      closeDialog,
      closeAllDialogs,
    }),
    [dialogs, openDialog, closeDialog, closeAllDialogs],
  );

  return (
    <DynamicDialogContext.Provider value={value}>
      {children}
      <DynamicDialogHost dialogs={dialogs} closeDialog={closeDialog} />
    </DynamicDialogContext.Provider>
  );
}

export function useDynamicDialog() {
  const context = useContext(DynamicDialogContext);

  if (!context) {
    throw new Error(
      "useDynamicDialog must be used inside DynamicDialogProvider",
    );
  }

  return context;
}

function DynamicDialogHost({ dialogs, closeDialog }) {
  return dialogs.map((dialog, index) => {
    const handleClose = () => closeDialog(dialog.id);

    return (
      <Dialog
        key={dialog.id}
        open
        onClose={handleClose}
        fullWidth={dialog.fullWidth ?? true}
        maxWidth={dialog.maxWidth ?? "lg"}
        BackdropProps={{
          sx: {
            zIndex: 1299 + index * 10,
          },
        }}
        sx={{
          "& .MuiDialog-paper": {
            zIndex: 1300 + index * 10,
            minHeight: dialog.minHeight || "auto",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          <span>{dialog.title || "Details"}</span>

          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers={dialog.dividers ?? true}>
          {typeof dialog.renderContent === "function"
            ? dialog.renderContent({
                dialog,
                closeDialog: handleClose,
              })
            : dialog.content || null}
        </DialogContent>

        {typeof dialog.renderActions === "function" ? (
          <DialogActions>
            {dialog.renderActions({
              dialog,
              closeDialog: handleClose,
            })}
          </DialogActions>
        ) : null}
      </Dialog>
    );
  });
}
